import {
  fetchBaseQuery,
  FetchBaseQueryError,
  BaseQueryFn
} from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import { TOKEN_CONSTANTS } from '../../constant';
import {
  getLocalStorage,
  setLocalStorage
} from '../../helpers/localstorage-utils';
import { BaseQueryWithReauthArgs, TokenPayload } from './basequery.type';

const mutex = new Mutex();

export const resetAuth = (): void => {
  localStorage.removeItem(TOKEN_CONSTANTS.REFRESH);
  localStorage.removeItem(TOKEN_CONSTANTS.ACCESS);
};

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_BASE_URL,
  prepareHeaders: (headers: Headers) => {
    const token = getLocalStorage(TOKEN_CONSTANTS.ACCESS);
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  }
});

const baseQueryWithReauth: BaseQueryFn<
  BaseQueryWithReauthArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  await mutex.waitForUnlock();
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const refreshToken = getLocalStorage(TOKEN_CONSTANTS.REFRESH);

        // try to get a new token
        const refreshResult = await baseQuery(
          {
            url: '/auth/refreshToken',
            method: 'POST',
            body: { refreshToken }
          },
          api,
          extraOptions
        );
        if (typeof refreshResult.data === 'object') {
          const { token, refreshToken } = refreshResult.data as TokenPayload;
          setLocalStorage(TOKEN_CONSTANTS.ACCESS, token);
          setLocalStorage(TOKEN_CONSTANTS.REFRESH, refreshToken);
        } else {
          resetAuth();
        }
      } catch (error) {
        resetAuth();
      } finally {
        release();
      }
    }

    // retry the initial query
    result = await baseQuery(args, api, extraOptions);
  }
  return result;
};

export default baseQueryWithReauth;
